import { Component, EventEmitter, Input, OnDestroy, Output } from "@angular/core";

/** Gives each instance its own element ids, so two copies on one page never share a label target. */
let nextId = 0;

/** "Test speaker": a soft two-note chime (C5 then G5), about a second long, in seconds from the start. */
const TEST_NOTES = [
  { frequency: 523.25, start: 0, end: 0.55 },
  { frequency: 783.99, start: 0.28, end: 1 },
];
const TEST_PEAK_GAIN = 0.2;
/** Silence that an exponential ramp can start from and fall to (it cannot reach 0). */
const TEST_FLOOR_GAIN = 0.0001;

type SinkAudioElement = HTMLAudioElement & { setSinkId?: (sinkId: string) => Promise<void> };

@Component({
  selector: "app-meeting-device-settings",
  standalone: false,
  templateUrl: "./meeting-device-settings.component.html",
  styleUrls: ["./meeting-device-settings.component.scss"],
})
export class MeetingDeviceSettingsComponent implements OnDestroy {
  @Input() mics: MediaDeviceInfo[] = [];
  @Input() speakers: MediaDeviceInfo[] = [];
  @Input() cameras: MediaDeviceInfo[] = [];
  @Input() audioInputId = "";
  @Input() audioOutputId = "";
  @Input() videoInputId = "";
  @Input() canChooseSpeaker = false;
  @Input() showDevices = true;
  @Input() showCamera = true;
  @Input() showEffects = false;
  /** Hide the section titles (and the card's title) when the surrounding panel already names the section. */
  @Input() headings = true;
  @Input() blurSupported = false;
  @Input() blur = false;
  @Input() audioDisabled = false;
  /** Mic level from 0 to 1. Null hides the meter. */
  @Input() level: number | null = null;
  /**
   * "panel" fills a side panel. "card" is the floating device card: a title, Speaker / Microphone /
   * Camera, and Test speaker + Done. The caller draws the card itself (background, radius, shadow,
   * position); the card pads itself 20px only while it shows its own title (`headings`).
   */
  @Input() variant: "panel" | "card" = "panel";
  @Output() micChange = new EventEmitter<string>();
  @Output() speakerChange = new EventEmitter<string>();
  @Output() cameraChange = new EventEmitter<string>();
  @Output() blurChange = new EventEmitter<boolean>();
  /** The card's Done button. */
  @Output() done = new EventEmitter<void>();

  readonly uid = `mtg-devices-${nextId++}`;
  testing = false;
  private testContext: AudioContext | null = null;
  private testAudio: HTMLAudioElement | null = null;
  private testTimer: ReturnType<typeof setTimeout> | null = null;

  /** The level as a bar width in percent; anything outside 0–1 or not a number is clamped. */
  get levelPercent(): number {
    const level = Number(this.level);
    if (!Number.isFinite(level)) return 0;
    return Math.round(Math.min(1, Math.max(0, level)) * 100);
  }

  ngOnDestroy() {
    this.stopTest();
  }

  label(device: MediaDeviceInfo, index: number, kind: string): string {
    return device.label || `${kind} ${index + 1}`;
  }

  selectedOr(id: string, devices: MediaDeviceInfo[]): string {
    return id && devices.some((device) => device.deviceId === id) ? id : devices[0]?.deviceId || "";
  }

  trackDevice(_: number, device: MediaDeviceInfo) {
    return device.deviceId;
  }

  valueOf(event: Event): string {
    return (event.target as HTMLSelectElement).value;
  }

  /**
   * Plays the chime through the selected speaker. WebAudio renders it into a MediaStream so an
   * audio element can route it with setSinkId; where that isn't supported, or the device is gone,
   * it plays on the default output.
   */
  async testSpeaker() {
    if (this.testing) return;
    const AudioContextClass =
      window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    this.testing = true;
    try {
      const context = new AudioContextClass();
      this.testContext = context;
      const destination = context.createMediaStreamDestination();
      const audio: SinkAudioElement = new Audio();
      this.testAudio = audio;
      audio.srcObject = destination.stream;

      const sinkId = this.canChooseSpeaker ? this.selectedOr(this.audioOutputId, this.speakers) : "";
      if (sinkId && typeof audio.setSinkId === "function") {
        await audio.setSinkId(sinkId).catch(() => undefined);
      }
      await audio.play();
      if (context.state === "suspended") await context.resume();
      // Destroyed, or the test was stopped, while waiting on the device.
      if (this.testContext !== context) return;

      const startAt = context.currentTime + 0.05;
      for (const note of TEST_NOTES) {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = "sine";
        oscillator.frequency.value = note.frequency;
        gain.gain.setValueAtTime(TEST_FLOOR_GAIN, startAt + note.start);
        gain.gain.exponentialRampToValueAtTime(TEST_PEAK_GAIN, startAt + note.start + 0.02);
        gain.gain.exponentialRampToValueAtTime(TEST_FLOOR_GAIN, startAt + note.end);
        oscillator.connect(gain).connect(destination);
        oscillator.start(startAt + note.start);
        oscillator.stop(startAt + note.end + 0.02);
      }
      const lastEnd = Math.max(...TEST_NOTES.map((note) => note.end));
      this.testTimer = setTimeout(() => this.stopTest(), (lastEnd + 0.25) * 1000);
    } catch {
      this.stopTest();
    }
  }

  private stopTest() {
    if (this.testTimer) clearTimeout(this.testTimer);
    this.testTimer = null;
    if (this.testAudio) {
      this.testAudio.pause();
      this.testAudio.srcObject = null;
      this.testAudio = null;
    }
    if (this.testContext) {
      this.testContext.close().catch(() => undefined);
      this.testContext = null;
    }
    this.testing = false;
  }
}
